import SiteFooter from './SiteFooter';
import SiteHeader from './SiteHeader';
import './PrivacyPage.css';

function TermsPage() {
  return (
    <main className="page-shell privacy-page">
      <SiteHeader />
      <section className="privacy-hero">
        <span className="eyebrow">Terms</span>
        <h1>Terms of Service</h1>
        <p>Last updated: 14.07.2026</p>
      </section>

      <section className="privacy-content" aria-label="Terms of service">
        <article className="privacy-intro">
          <p>Welcome to Crumbs.</p>
          <p>
            Crumbs is a private app for sharing small weekly updates, photos, notes,
            comments, and moments with the people you choose.
          </p>
          <p>
            By using Crumbs, you agree to these Terms of Service. If you do not agree,
            please do not use the app.
          </p>
        </article>

        <PolicySection title="1. Who can use Crumbs">
          <p>You must be at least 13 years old to use Crumbs.</p>
          <p>
            By using Crumbs, you confirm that you are old enough to use the app and that
            you have the right to agree to these Terms.
          </p>
        </PolicySection>

        <PolicySection title="2. Your account">
          <p>You may need to create an account to use Crumbs.</p>
          <p>
            You are responsible for keeping your account secure and for any activity that
            happens through your account.
          </p>
          <p>
            Please use accurate information when creating your account and do not use
            someone else's account without permission.
          </p>
          <p>
            If you believe your account has been accessed without your permission, contact
            us at [your email].
          </p>
        </PolicySection>

        <PolicySection title="3. Your content">
          <p>
            Crumbs lets you upload and share content such as photos, captions, notes,
            comments, reactions, and other small updates.
          </p>
          <p>You keep ownership of the content you add to Crumbs.</p>
          <p>
            By uploading content to Crumbs, you give us permission to store, display,
            process, and share that content only as needed to provide and operate the app.
            For example, we need to show your crumbs to the circles or people you choose
            to share them with.
          </p>
          <p>You are responsible for the content you upload.</p>
          <p>Please do not upload content that:</p>
          <ul>
            <li>belongs to someone else unless you have permission;</li>
            <li>is illegal, harmful, abusive, hateful, threatening, or harassing;</li>
            <li>violates someone's privacy;</li>
            <li>contains nudity, sexual content, or graphic violence;</li>
            <li>impersonates another person;</li>
            <li>contains spam, scams, malware, or harmful code;</li>
            <li>breaks any applicable law or regulation.</li>
          </ul>
          <p>We may remove content or restrict accounts if we believe these Terms have been violated.</p>
        </PolicySection>

        <PolicySection title="4. Private circles and sharing">
          <p>Crumbs is designed for private sharing.</p>
          <p>
            Your crumbs are shared with the circles or people you choose. However, you
            understand that people who can see your content may save, screenshot, copy, or
            share it outside Crumbs.
          </p>
          <p>
            Please only share content that you are comfortable sharing with the people in
            your selected circle.
          </p>
          <p>Crumbs cannot control what other users do with content after they have access to it.</p>
        </PolicySection>

        <PolicySection title="5. Comments and interactions">
          <p>People in your circles may be able to comment on, react to, or view your crumbs.</p>
          <p>
            You agree to use Crumbs respectfully and not to harass, bully, threaten,
            shame, or harm other users.
          </p>
          <p>
            We want Crumbs to feel safe, gentle, and private. If someone uses the app in a
            way that harms others, we may remove their content or suspend their account.
          </p>
        </PolicySection>

        <PolicySection title="6. Invites and circles">
          <p>You may be able to invite other people to join your circles through links or other invite methods.</p>
          <p>You are responsible for sharing invite links only with people you want to include.</p>
          <p>
            If someone joins a circle through an invite link, they may be able to see
            content shared inside that circle, depending on how the app works at that
            time.
          </p>
        </PolicySection>

        <PolicySection title="7. App availability">
          <p>We are still building and improving Crumbs.</p>
          <p>
            We may change, pause, remove, or update features at any time. We may also
            temporarily suspend the app for maintenance, updates, security, or technical
            reasons.
          </p>
          <p>
            We do our best to keep Crumbs available, but we cannot guarantee that the app
            will always be available, error-free, or uninterrupted.
          </p>
        </PolicySection>

        <PolicySection title="8. Beta or early access">
          <p>Crumbs may be released as a beta, test, or early version.</p>
          <p>This means some features may change, break, disappear, or not work as expected.</p>
          <p>
            By using a beta or early version, you understand that the app is still being
            tested and improved.
          </p>
        </PolicySection>

        <PolicySection title="9. Feedback">
          <p>
            If you send us ideas, suggestions, feedback, or feature requests, you allow us
            to use them to improve Crumbs without owing you payment or credit.
          </p>
          <p>We appreciate your feedback, but we are not required to use every suggestion.</p>
        </PolicySection>

        <PolicySection title="10. Intellectual property">
          <p>
            Crumbs, including its name, design, branding, interface, features, text,
            graphics, and other materials, belongs to us or our licensors.
          </p>
          <p>
            You may not copy, modify, reproduce, distribute, reverse engineer, or create
            derivative works from Crumbs without our permission, except where the law
            allows it.
          </p>
        </PolicySection>

        <PolicySection title="11. What you should not do">
          <p>You agree not to:</p>
          <ul>
            <li>use Crumbs for illegal or harmful purposes;</li>
            <li>try to access accounts, data, or systems that do not belong to you;</li>
            <li>interfere with the security or operation of the app;</li>
            <li>scrape, copy, or collect data from Crumbs without permission;</li>
            <li>upload viruses, malware, or harmful code;</li>
            <li>impersonate another person;</li>
            <li>use Crumbs to harass, threaten, or harm others;</li>
            <li>violate these Terms or any applicable law.</li>
          </ul>
        </PolicySection>

        <PolicySection title="12. Deleting your account">
          <p>You can request deletion of your account by contacting us at:</p>
          <p>
            <a href="mailto:crmbsapp@gmail.com">crmbsapp@gmail.com</a>
          </p>
          <p>
            When your account is deleted, we will delete or anonymise your personal
            information where reasonably possible, unless we need to keep certain
            information for legal, security, or operational reasons.
          </p>
          <p>Some content may remain visible temporarily or in backups for a limited time.</p>
        </PolicySection>

        <PolicySection title="13. Privacy">
          <p>Your privacy matters to us.</p>
          <p>
            Please read our <a href="/privacy">Privacy Policy</a> to understand how we
            collect, use, store, and protect your information.
          </p>
        </PolicySection>

        <PolicySection title="14. Third-party services">
          <p>
            Crumbs may use third-party services for things like authentication, hosting,
            analytics, storage, notifications, crash reporting, or app infrastructure.
          </p>
          <p>We are not responsible for third-party services that we do not control.</p>
          <p>Your use of those services may also be subject to their own terms and privacy policies.</p>
        </PolicySection>

        <PolicySection title="15. No warranties">
          <p>Crumbs is provided "as is" and "as available."</p>
          <p>
            We do not guarantee that Crumbs will always be secure, available, accurate,
            error-free, or suitable for your specific needs.
          </p>
          <p>You use Crumbs at your own risk.</p>
        </PolicySection>

        <PolicySection title="16. Limitation of liability">
          <p>
            To the fullest extent allowed by law, we will not be responsible for indirect,
            incidental, special, consequential, or punitive damages, including loss of
            data, loss of content, loss of profits, or harm caused by your use of or
            inability to use Crumbs.
          </p>
          <p>Nothing in these Terms limits rights that cannot legally be limited.</p>
        </PolicySection>

        <PolicySection title="17. Changes to these Terms">
          <p>We may update these Terms from time to time.</p>
          <p>
            If we make significant changes, we may notify you in the app, by email, or by
            updating the date at the top of this page.
          </p>
          <p>By continuing to use Crumbs after the Terms change, you agree to the updated Terms.</p>
        </PolicySection>

        <PolicySection title="18. Ending your access">
          <p>
            We may suspend or terminate your access to Crumbs if we believe you have
            violated these Terms, created risk for other users, or used the app in a
            harmful or unlawful way.
          </p>
          <p>You may stop using Crumbs at any time.</p>
        </PolicySection>

        <PolicySection title="19. Governing law">
          <p>These Terms are governed by the laws of England and Wales, unless another law applies.</p>
          <p>
            Any disputes will be handled in the courts of England and Wales, unless
            applicable law gives you rights to bring a claim elsewhere.
          </p>
        </PolicySection>

        <PolicySection title="20. Contact us">
          <p>If you have questions about these Terms, contact us at:</p>
          <p>
            <a href="mailto:crmbsapp@gmail.com">crmbsapp@gmail.com</a>
          </p>
        </PolicySection>
      </section>
      <SiteFooter />
    </main>
  );
}

function PolicySection({ title, children }) {
  return (
    <article>
      <h2>{title}</h2>
      {children}
    </article>
  );
}

export default TermsPage;
